/**
 * The one place a match report's completeness status becomes words AND colour.
 *
 * SE-7286 AC#3: wherever the status appears — the capture header, the Reports
 * list, the submitted view and match detail — it is shown WITH its
 * denominator, never as a bare word. Those four surfaces each composed the
 * string themselves, which is how `Complete` came to carry no count on all
 * four while `Partial` carried one on all four: nothing held them together, so
 * there was nothing for the odd one out to disagree with.
 *
 * **SE-8052 brought the TONE here for the same reason, after the same thing
 * happened to it.** The words were centralised and the colour was not, so the
 * pairing went on being retyped per surface — three copies, and by the time
 * they were counted one had already drifted: the quick-capture header wore
 * `success` (the RATING annex — score-band colour, whose whole justification
 * in `tailwind.config.js` is that it is *not* interface vocabulary) where the
 * Reports list and the submitted view wore `success-soft`. So one workflow
 * verdict rendered in two different greens, and one of those greens was the
 * vocabulary the design system reserves for measurements.
 *
 * A status is not a measurement, and after this module owns both halves there
 * is no longer a place for a fourth surface to disagree from.
 *
 * **The pair is `scored` of `ticked`, and it is not the pair that shipped.**
 * The old labels read `notedCount of squadSize` — coverage, not scoring — which
 * was defensible while a "noted" player meant one somebody had written about.
 * Under the SE-7286 ruling (Raniel, 2026-08-22) squad MEMBERSHIP sets the tick,
 * so `notedCount` now means only "on the report", and that pair renders
 * `Partial · 28 of 28`: a verdict contradicting its own numbers. The ruling
 * states the right pair outright — *"seed squad of 5, score 1 → PARTIAL · 1 of
 * 5"*. Measured on an S24: 28 ticked, 1 scored, server verdict PARTIAL.
 *
 * The denominator is the scout's OWN selection, never the whole squad. Someone
 * who ticked 5 of a 28-player squad and scored all 5 has finished what they set
 * out to do, and `5 of 28` would call that unfinished.
 *
 * **This also overturns a documented earlier decision.** The capture header's
 * test asserted the bare word on purpose, reasoning that *"the count belongs to
 * Partial only — 'Complete · 50 of 50' invites the reader to check the
 * arithmetic instead of reading the verdict"*. Checking the arithmetic is now
 * the POINT: the defect the ruling exists to fix was a report that scored 1
 * player of a 59-player squad and stated COMPLETE — a confident verdict the
 * reader had no way to audit.
 * @module utils/toReportStatusLabel
 */
import type { PillTone } from "@/components/atoms/Badge";

/** The states a match report's status pill can be in. */
export type ReportStatusKey = "draft" | "complete" | "partial";

/**
 * The pairing a MEASUREMENT wears — a score, or a count of players.
 *
 * Named here, in the module that owns the status vocabulary, because it is the
 * thing a status must never be mistaken for. `ReportListRow` puts the count
 * pill (`3 players`) directly beside the status pill in the same row, and
 * `ReportPlayerRow` dresses `Perf 8` / `Pot 9` the same way, so a reader who
 * cannot tell the two pairings apart has no way to know which chip is a number
 * somebody produced and which is a state the report is in.
 *
 * Exported so the invariant can be executed rather than asked for on trust:
 * `toReportStatusTone.test` compares every status against this constant, and
 * against the classes it resolves to, instead of restating the rule in prose.
 */
export const MEASUREMENT_PILL_TONE: PillTone = "neutral-raised";

/**
 * The tone each status wears.
 *
 * A `Record`, so the set is closed, and the values are chosen — not sampled.
 * Every one of these is a token NAME: the scale inverts between themes and
 * `success-soft`'s ground reads near-white in light and near-black in dark, so
 * a transcribed hex would paint one theme's ground into the other's text.
 *
 * **Why green for `complete`, when the surface this was filed against showed
 * grey.** Two of the three pill surfaces already rendered `success-soft`, and
 * the filed defect names the Reports list's green as the thing the other
 * surfaces disagreed with, so the majority and the report point the same way.
 * The tie-breaker is which tier the colour comes from: `success-soft` resolves
 * through the `success` family in the semantic tier, while the divergent
 * `success` tone resolves through the rating annex. A verdict on filed work
 * belongs to interface semantics; a score band does not.
 *
 * **`partial` is untouched.** Its amber was already correct on every surface
 * and SE-8052 puts it out of scope explicitly — it is the control that proved
 * flat grey was not a deliberate house style for statuses.
 *
 * **`draft` stays outline-only**, which is the one status that is deliberately
 * quiet: an unfiled report has no verdict yet, and a filled ground would state
 * one. It is still not the measurement pairing — an outline with no ground
 * cannot be confused with a filled chip.
 */
const STATUS_TONE: Record<ReportStatusKey, PillTone> = {
  draft: "neutral-outline",
  complete: "success-soft",
  partial: "warning-subtle",
};

/**
 * The same three verdicts as a TEXT colour, for surfaces that draw no ground.
 *
 * Byte-identical to what the phone header already rendered for `complete` and
 * `partial`; `draft` is the one that changes, and it changes from green to a
 * neutral because green is a verdict an unfiled report has not earned.
 */
const STATUS_TEXT_CLASS: Record<ReportStatusKey, string> = {
  draft: "text-content-secondary",
  complete: "text-status-success",
  partial: "text-status-warning",
};

/**
 * The tone the status pill wears, for every surface that renders one.
 *
 * Callers pass the same `ReportStatusKey` they pass `toReportStatusLabel`, so
 * a surface cannot end up labelling one status and colouring another.
 * @param status - Which state the report is in.
 * @returns The pill tone.
 */
export const toReportStatusTone = (status: ReportStatusKey): PillTone =>
  STATUS_TONE[status];

/**
 * The label colour for a surface that states the verdict as TEXT, not a pill.
 *
 * The phone's capture header is the one such surface: at phone width the
 * desktop toolbar is not rendered at all (`isMobile ? null : <QuickCaptureHeader/>`)
 * and the verdict appears as a coloured line inside `MobileCaptureHeader`
 * instead. A pill tone cannot express that — it carries a ground the phone
 * header does not draw — so the shared module owns this shape too rather than
 * leaving the one surface it does not fit to decide for itself. That is the
 * hole every previous round of this bug came through.
 *
 * Same colour FAMILY as the pill: success for a complete verdict, warning for
 * a partial one, so the two surfaces agree about what the status means even
 * though they draw it differently.
 *
 * **`draft` is why this is a `Record` and not a ternary.** The phone header
 * asked `status === "partial" ? warning : success`, which is a two-way answer
 * to a three-way question: a draft fell to the `else` and would have rendered
 * GREEN — the strongest verdict available — for the one state that has earned
 * no verdict at all. Unreachable today behind an `isSubmitted` guard, and
 * exactly the shape that stops being unreachable when someone moves the guard.
 * @param status - Which state the report is in.
 * @returns The text colour class.
 */
export const toReportStatusTextClass = (status: ReportStatusKey): string =>
  STATUS_TEXT_CLASS[status];

/**
 * Builds the status pill's text.
 *
 * `Draft` deliberately carries no count. Completeness is a verdict on FILED
 * work, so a denominator on a report still being written would state one
 * before the scout has finished — the same reasoning that keeps the capture
 * header's pill hidden until submit.
 * **SE-7550: the label names its numerator.** It used to read
 * `Partial · 1 of 2`, which is correct and unreadable beside the coverage
 * pill's `2/70 noted` — two bare "x of y" strings, neither saying what it
 * counted. `toSquadCoverageLabel` owns the other half of that pair, and the
 * two must never end in the same word.
 * @param status - Which state the report is in.
 * @param scoredCount - Players carrying a Performance score. The NUMERATOR.
 * @param tickedCount - Players the scout put on the report. The DENOMINATOR,
 * or null when the surface does not know it.
 * @returns The pill's label, ending in the word it counts.
 */
export const toReportStatusLabel = (
  status: ReportStatusKey,
  scoredCount: number,
  tickedCount: number | null
): string => {
  if (status === "draft") return "Draft";

  const verdict = status === "complete" ? "Complete" : "Partial";

  // The denominator can genuinely be unknown, and "Complete · 3 of null" is
  // the shape of bug this branch guards. The bare count is still better than
  // the bare word: it is the number that makes the verdict checkable.
  return tickedCount === null
    ? `${verdict} · ${scoredCount} scored`
    : `${verdict} · ${scoredCount} of ${tickedCount} scored`;
};
